import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Trash2, Clock, ShieldCheck } from 'lucide-react';
import { useBackTarget } from '../../hooks/useBackTarget';

const font = "'Inria Serif', serif";
const SUPPORT_EMAIL = 'contact@allurexp.com';
const LAST_UPDATED = 'February 1, 2026';

const sectionStyle = { marginTop: 28 };
const h2Style = {
  fontFamily: font,
  fontSize: 22,
  fontWeight: 700,
  color: '#111',
  margin: '0 0 10px',
};
const h3Style = {
  fontFamily: font,
  fontSize: 17,
  fontWeight: 700,
  color: '#111',
  margin: '16px 0 6px',
};
const pStyle = {
  fontFamily: font,
  fontSize: 15,
  lineHeight: 1.6,
  color: '#333',
  margin: '0 0 10px',
};
const listStyle = {
  fontFamily: font,
  fontSize: 15,
  lineHeight: 1.7,
  color: '#333',
  paddingLeft: 22,
  margin: '0 0 10px',
};

const stepCardStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  padding: 16,
  border: '1px solid #ECECF1',
  borderRadius: 14,
  background: '#FAFAFC',
  marginBottom: 12,
};

const iconBubble = {
  flexShrink: 0,
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#F1ECFF',
  color: '#703FF6',
};

export default function DeleteAccountPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Delete your Allure account';
  }, []);

  const subject = encodeURIComponent('Account deletion request');
  const body = encodeURIComponent(
    `Hello Allure team,\n\nI would like to permanently delete my Allure account and all associated personal data.\n\nAccount email: \nRegistered name: \nRole (guest / host / venue): \nReason (optional): \n\nThank you.`
  );
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;

  return (
    <div
      data-testid="delete-account-page"
      style={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        padding: '20px 20px 80px',
        maxWidth: 820,
        margin: '0 auto',
      }}
    >
      <button
        type="button"
        onClick={goBack}
        data-testid="delete-account-back-button"
        aria-label="Back"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: 'none',
          background: 'transparent',
          fontFamily: font,
          fontSize: 15,
          color: '#703FF6',
          cursor: 'pointer',
          padding: 0,
          marginBottom: 14,
        }}
      >
        <ArrowLeft size={18} /> Back
      </button>

      <h1
        data-testid="delete-account-title"
        style={{
          fontFamily: font,
          fontSize: 30,
          fontWeight: 700,
          color: '#111',
          margin: 0,
        }}
      >
        Delete your Allure account
      </h1>
      <p style={{ ...pStyle, color: '#666', marginTop: 8 }}>
        Last updated: {LAST_UPDATED}
      </p>

      <section style={sectionStyle}>
        <p style={pStyle}>
          This page explains how to request the deletion of your Allure account and the personal
          data associated with it. The Allure app is operated by the team behind{' '}
          <a href="https://allurexp.com" style={{ color: '#703FF6' }}>allurexp.com</a>.
          The deletion process is the same for guests, hosts, venue owners and staff accounts.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>How to request deletion</h2>
        <p style={pStyle}>
          You can submit a deletion request in two ways. Both reach the same support inbox and are
          handled within the timeframes described below.
        </p>

        <div style={stepCardStyle}>
          <div style={iconBubble}><Mail size={20} /></div>
          <div>
            <div style={{ ...h3Style, margin: 0 }}>1. Email us</div>
            <p style={{ ...pStyle, marginTop: 6 }}>
              Send a short message to{' '}
              <a href={mailto} data-testid="delete-account-mailto" style={{ color: '#703FF6', fontWeight: 600 }}>
                {SUPPORT_EMAIL}
              </a>
              {' '}from the email address you used to sign up. Include your registered name and
              role (guest, host or venue) so we can locate your account.
            </p>
            <a
              href={mailto}
              data-testid="delete-account-email-button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 6,
                padding: '10px 16px',
                background: '#703FF6',
                color: '#fff',
                fontFamily: font,
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 10,
                textDecoration: 'none',
              }}
            >
              <Mail size={16} /> Open prefilled email
            </a>
          </div>
        </div>

        <div style={stepCardStyle}>
          <div style={iconBubble}><Trash2 size={20} /></div>
          <div>
            <div style={{ ...h3Style, margin: 0 }}>2. From inside the app</div>
            <p style={{ ...pStyle, marginTop: 6 }}>
              Open Allure → tap your profile and follow the path for your role:
            </p>
            <ul style={listStyle}>
              <li><strong>Guest:</strong> Profile → Settings → Privacy → <em>Delete my account</em>.</li>
              <li><strong>Host:</strong> Profile → Settings → Privacy → <em>Delete my account</em>.</li>
              <li><strong>Venue:</strong> Venue Settings → scroll to the bottom → <em>Delete my account</em>.</li>
            </ul>
            <p style={{ ...pStyle, marginTop: 6 }}>
              Confirm with your password and type <strong>DELETE</strong>. Your account will be
              queued for deletion immediately.
            </p>
          </div>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>What gets deleted</h2>
        <p style={pStyle}>When your deletion request is processed we permanently remove:</p>
        <ul style={listStyle}>
          <li>Your name, email address, phone number and profile photo.</li>
          <li>Your guest pass (QR code), tier history and saved preferences.</li>
          <li>Your messages, in-app chat history and notification tokens.</li>
          <li>Photos you uploaded in venue reviews, profile or tip flows.</li>
          <li>Device identifiers, login sessions and approximate location records.</li>
          <li>For hosts and venue staff: your dashboard credentials and personal stats.</li>
        </ul>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>What may be retained (and why)</h2>
        <p style={pStyle}>
          Some records are kept after account deletion only when we are legally required to do so
          or to protect the rights of other users:
        </p>
        <ul style={listStyle}>
          <li>
            <strong>Financial / transaction records</strong> — tax and anti-fraud law requires us
            to retain payment records (amount, date, venue, anonymised buyer reference) for up to{' '}
            <strong>10 years</strong>. These records are detached from your personal identity.
          </li>
          <li>
            <strong>Tip payouts to hosts</strong> — payout traces required by our payment
            processor (Viva Wallet) are retained according to PSD2 and local accounting rules.
          </li>
          <li>
            <strong>Aggregated, non-identifying analytics</strong> (e.g. "X scans on Y date") may
            be retained indefinitely as they cannot be traced back to you.
          </li>
          <li>
            <strong>Public reviews</strong> you have left for venues are anonymised
            (name replaced by "Former Allure user"). You can ask us to remove the review content
            entirely as part of your deletion request.
          </li>
        </ul>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>How long it takes</h2>
        <div style={stepCardStyle}>
          <div style={iconBubble}><Clock size={20} /></div>
          <div>
            <p style={{ ...pStyle, margin: 0 }}>
              When you submit a deletion request, your account immediately enters a{' '}
              <strong>14-day grace period</strong>. During this time you can still sign in and
              cancel the deletion if you change your mind. After 14 days, all personal data is
              permanently anonymised within <strong>30 days</strong> and encrypted backups are
              rotated within <strong>90 days</strong>, after which no copy of your personal data
              remains.
            </p>
          </div>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Your rights</h2>
        <div style={stepCardStyle}>
          <div style={iconBubble}><ShieldCheck size={20} /></div>
          <div>
            <p style={{ ...pStyle, margin: 0 }}>
              Under the EU GDPR, the UK Data Protection Act 2018 and the California Consumer
              Privacy Act (CCPA), you have the right to access, correct, export and delete your
              personal data at any time and free of charge. If you believe we have not honoured
              your rights you can contact your local data protection authority. Full details are
              in our{' '}
              <a href="/privacy" style={{ color: '#703FF6' }}>Privacy Policy</a>.
            </p>
          </div>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Contact</h2>
        <p style={pStyle}>
          For any question about deletion, account recovery or data export, write to{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#703FF6' }}>{SUPPORT_EMAIL}</a>.
        </p>
      </section>
    </div>
  );
}
